import React, { useState, useRef, useEffect } from 'react';
import api from '../http';

/**
 * Inline, expandable skill checklist card for the testing view.
 * Receives pre-loaded checklist data from the session detail response
 * so no additional fetch is needed on mount.
 *
 * Props:
 *   assignment   - skill assignment object (from session.skill_assignments)
 *                  must include: id, skill_name, candidate_id, checklist_items[], item_results{}
 *   candidateName - display name for the candidate (null for team/company skills)
 *   sessionId    - for the complete-skill API call
 *   timer        - elapsed seconds shared from parent (session-wide)
 *   onComplete    - callback(assignmentId, result) after skill is submitted
 *   collapseAll   - counter incremented by parent to collapse all cards at once
 */
export default function SkillChecklistCard({
  assignment,
  candidateName,
  sessionId,
  timer,
  onComplete,
  collapseAll
}) {
  const [expanded, setExpanded] = useState(false);
  const [itemResults, setItemResults] = useState(assignment.item_results || {});
  const [saving, setSaving] = useState(false);

  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);

  // Collapse when parent increments collapseAll
  useEffect(() => {
    if (collapseAll > 0) setExpanded(false);
  }, [collapseAll]);

  const checklistItems = assignment.checklist_items || [];
  const passedCount = Object.values(itemResults).filter((v) => v === true).length;
  const failedCount = Object.values(itemResults).filter((v) => v === false).length;
  const totalCount = checklistItems.length;
  const unmarkedCount = totalCount - passedCount - failedCount;

  const groupedItems = checklistItems.reduce((acc, item) => {
    const cat = item.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const handleItemResult = async (itemId, passed) => {
    // Compute new results synchronously so we can check completion immediately
    const newResults = { ...itemResults, [itemId]: passed };
    setItemResults(newResults);

    try {
      await api.post(
        `/api/test-sessions/skill-assignment/${assignment.id}/item`,
        { checklist_item_id: itemId, passed },
        { queueable: true }
      );
    } catch {
      // Roll back optimistic update on failure
      setItemResults((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      return; // Don't auto-complete if the save failed
    }

    // Auto-complete the skill once every checklist item is marked.
    // This means the proctor doesn't need to separately click ✗ Fail / ✓ Pass after
    // going through each item — the result is inferred: fail if any item failed, else pass.
    if (checklistItems.length > 0 && !assignment.result && !saving) {
      const allMarked = checklistItems.every((item) => newResults[item.id] !== undefined);
      if (allMarked) {
        const anyFailed = Object.values(newResults).some((v) => v === false);
        const autoResult = anyFailed ? 'fail' : 'pass';
        const autoPassedCount = Object.values(newResults).filter((v) => v === true).length;
        const autoFailedCount = Object.values(newResults).filter((v) => v === false).length;

        setSaving(true);
        try {
          await api.post(
            `/api/test-sessions/${sessionId}/complete-skill`,
            {
              assignment_id: assignment.id,
              result: autoResult,
              passed_items: autoPassedCount,
              failed_items: autoFailedCount,
              time_seconds: timer
            },
            { queueable: true }
          );
          onComplete(assignment.id, autoResult);
        } catch {
          // Silent failure — proctor can still click Fail/Pass manually
        } finally {
          setSaving(false);
        }
      }
    }
  };

  const handleClearItem = async (itemId) => {
    const prev = itemResults[itemId];
    setItemResults((prevResults) => {
      const next = { ...prevResults };
      delete next[itemId];
      return next;
    });
    try {
      await api.delete(`/api/test-sessions/skill-assignment/${assignment.id}/item/${itemId}`, {
        queueable: true
      });
    } catch {
      setItemResults((prevResults) => ({ ...prevResults, [itemId]: prev }));
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    const itemEl = e.target.closest('.checklist-item');
    if (itemEl) {
      itemEl.style.transform = `translateX(${Math.min(100, Math.max(-100, diff))}px)`;
      itemEl.style.opacity = 1 - Math.abs(diff) / 200;
      if (diff > 50) {
        itemEl.classList.add('swiping-pass');
        itemEl.classList.remove('swiping-fail');
      } else if (diff < -50) {
        itemEl.classList.add('swiping-fail');
        itemEl.classList.remove('swiping-pass');
      } else {
        itemEl.classList.remove('swiping-pass', 'swiping-fail');
      }
    }
  };

  const handleTouchEnd = (e, itemId) => {
    const diff = touchCurrentX.current - touchStartX.current;
    const itemEl = e.target.closest('.checklist-item');
    if (itemEl) {
      itemEl.style.transform = '';
      itemEl.style.opacity = '';
      itemEl.classList.remove('swiping-pass', 'swiping-fail');
    }
    if (Math.abs(diff) > 80) {
      handleItemResult(itemId, diff > 0);
    }
    touchStartX.current = 0;
    touchCurrentX.current = 0;
  };

  const handlePassToggle = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      if (assignment.result === 'pass') {
        // Already passed — clear the skill result
        await api.delete(
          `/api/test-sessions/${sessionId}/skill-assignment/${assignment.id}/result`,
          { queueable: true }
        );
        // Clear all item results locally (the result is no longer meaningful)
        setItemResults({});
        onComplete(assignment.id, null);
      } else {
        // Mark every checklist item as passed first (optimistic + parallel API calls)
        if (checklistItems.length > 0) {
          const allPassed = {};
          checklistItems.forEach((item) => { allPassed[item.id] = true; });
          setItemResults(allPassed);
          // Fire all item saves in parallel — don't block skill completion on them
          Promise.allSettled(
            checklistItems.map((item) =>
              api.post(
                `/api/test-sessions/skill-assignment/${assignment.id}/item`,
                { checklist_item_id: item.id, passed: true },
                { queueable: true }
              )
            )
          );
        }
        // Complete the skill as passed
        await api.post(
          `/api/test-sessions/${sessionId}/complete-skill`,
          {
            assignment_id: assignment.id,
            result: 'pass',
            passed_items: totalCount,
            failed_items: 0,
            time_seconds: timer
          },
          { queueable: true }
        );
        onComplete(assignment.id, 'pass');
      }
    } catch {
      alert('Failed to update skill result');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (result) => {
    // Note: we no longer prompt for unmarked items — the count is visible in the
    // progress bar, and window.confirm can be auto-dismissed on tablet WebViews.
    setSaving(true);
    try {
      await api.post(
        `/api/test-sessions/${sessionId}/complete-skill`,
        {
          assignment_id: assignment.id,
          result,
          passed_items: passedCount,
          failed_items: failedCount,
          time_seconds: timer
        },
        { queueable: true }
      );
      onComplete(assignment.id, result);
    } catch {
      alert('Failed to record skill result');
    } finally {
      setSaving(false);
    }
  };

  const isPassed = assignment.result === 'pass';
  const isFailed = assignment.result === 'fail';

  return (
    <div className={`checklist-card ${isPassed ? 'completed pass' : isFailed ? 'completed fail' : ''}`}>
      {/* Card header — tap to expand/collapse */}
      <div className="checklist-card-header" onClick={() => setExpanded((v) => !v)}>
        <div className="card-header-left">
          <span className="card-skill-name">{assignment.skill_name}</span>
          {candidateName && <span className="card-candidate">{candidateName}</span>}
          {isFailed && <span className="result-badge fail">✗ Fail</span>}
        </div>
        <div className="card-header-right">
          {totalCount > 0 && !isPassed && (
            <span className="card-progress">
              {passedCount + failedCount}/{totalCount}
            </span>
          )}
          <button
            className={`approve-all-btn ${isPassed ? 'active' : ''}`}
            onClick={handlePassToggle}
            disabled={saving}
            title={isPassed ? 'Click to clear pass' : 'Pass all items'}
          >
            ✓ Pass
          </button>
          <button
            className="expand-toggle-btn"
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="checklist-card-body">
          {/* Progress bar */}
          {totalCount > 0 && (
            <>
              <div className="progress-bar">
                <div className="progress-pass" style={{ width: `${(passedCount / totalCount) * 100}%` }} />
                <div className="progress-fail" style={{ width: `${(failedCount / totalCount) * 100}%` }} />
              </div>
              <div className="progress-labels">
                <span className="pass-label">✓ {passedCount}</span>
                <span className="unmarked-label">{unmarkedCount} remaining</span>
                <span className="fail-label">✗ {failedCount}</span>
              </div>
              <div className="swipe-hint">
                <span>← Swipe left to fail</span>
                <span>Swipe right to pass →</span>
              </div>
            </>
          )}

          {/* Checklist items */}
          <div className="checklist-items">
            {Object.entries(groupedItems).map(([category, items]) => (
              <div key={category} className="checklist-category">
                {Object.keys(groupedItems).length > 1 && (
                  <h4 className="category-header">{category}</h4>
                )}
                {items
                  .sort((a, b) => (a.item_order || 0) - (b.item_order || 0))
                  .map((item) => {
                    const result = itemResults[item.id];
                    return (
                      <div
                        key={item.id}
                        className={`checklist-item ${result === true ? 'passed' : result === false ? 'failed' : ''}`}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={(e) => handleTouchEnd(e, item.id)}
                      >
                        <div className="item-content">
                          <div className="item-text">{item.description}</div>
                          {item.is_critical && <span className="critical-badge">Critical</span>}
                        </div>
                        <div className="item-actions">
                          <button
                            className={`action-btn pass ${result === true ? 'active' : ''}`}
                            onClick={() => handleItemResult(item.id, true)}
                          >
                            ✓
                          </button>
                          <button
                            className={`action-btn fail ${result === false ? 'active' : ''}`}
                            onClick={() => handleItemResult(item.id, false)}
                          >
                            ✗
                          </button>
                          {result !== undefined && (
                            <button className="action-btn clear" onClick={() => handleClearItem(item.id)}>
                              ↺
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ))}

            {checklistItems.length === 0 && (
              <p className="no-checklist-note">No checklist items defined for this skill.</p>
            )}
          </div>

          {/* Complete buttons */}
          <div className="complete-actions">
            <button
              className="complete-btn fail"
              onClick={() => handleComplete('fail')}
              disabled={saving}
            >
              ✗ Fail
            </button>
            <button
              className="complete-btn pass"
              onClick={() => handleComplete('pass')}
              disabled={saving}
            >
              ✓ Pass
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
